import { Check } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ThemeIcon, getThemeLabel } from '@/components/ui/theme-icon'
import { useTheme } from '@/hooks'
import { cn } from '@/lib/utils'

import type { Theme } from '@/stores/ui.store'

interface ThemeSwitcherProps {
  /** 自定义类名 */
  className?: string
}

/** 可用的主题选项 */
const THEME_OPTIONS: Theme[] = ['light', 'dark', 'system']

/**
 * 主题切换器组件
 * 
 * 提供下拉菜单让用户选择主题（亮色/暗色/跟随系统）
 * 
 * @example
 * ```tsx
 * <ThemeSwitcher />
 * ```
 */
export function ThemeSwitcher({ className }: ThemeSwitcherProps) {
  const { theme, resolvedTheme, setTheme } = useTheme()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-9 w-9', className)}
          aria-label="切换主题"
          data-theme-switcher
        >
          {/* 显示当前解析后的主题图标 */}
          <ThemeIcon theme={resolvedTheme} className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {THEME_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option}
            onClick={() => setTheme(option)}
            className="flex items-center gap-2"
          >
            <ThemeIcon theme={option} className="h-4 w-4" />
            <span>{getThemeLabel(option)}</span>
            {theme === option && (
              <Check className="ml-auto h-4 w-4" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
